import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTranslation } from "react-i18next";
import { sprintf } from "utils/strings";
import httpClient from "api/httpClient";
import ConfirmationDialog from "components/Common/ConfirmationDialog";
import { CustomTablePagination } from "components/Common/CustomTablePagination";
import { CustomMenuButton } from "components/Common/CustomMenuButton";
import SchemaDialogForm from "components/Form/SchemaDialogForm";
import HospitalsTableData from "./data/HospitalsTableData";
import { hospitalsFormSchema, hospitalsRecord } from "./data/HospitalsSchemas";
import { handleApiError } from "utils/apiError";

function Hospitals() {
  const apiBase = process.env.REACT_APP_API_URL;
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(hospitalsRecord);
  const [dialogTitle, setDialogTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarColor, setSnackBarColor] = useState("error");
  const [snackErrorMsg, setSnackErrorMsg] = useState("");
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const showSnackBar = (title, type, message) => {
    setSnackBarColor(type === "error" ? "error" : "success");
    setSnackErrorMsg(message);
    setSnackBarOpen(true);
  };

  const loadHospitals = async () => {
    try {
      setLoading(true);
      const url = `${apiBase}/api/hospitals?page=${page}&size=${rowsPerPage}&q=${searchInput}`;
      const res = await httpClient.get(url);
      setItems(res.data.content || []);
      setTotalElements(res.data.totalElements || 0);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHospitals();
  }, [page, rowsPerPage]);

  const openCreateDialog = () => {
    setEditingRecord(hospitalsRecord);
    setDialogTitle("Create Hospital");
    setDialogOpen(true);
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      if (data.id > 0) {
        await httpClient.put(`${apiBase}/api/hospitals/${data.id}`, data);
      } else {
        await httpClient.post(`${apiBase}/api/hospitals`, data);
      }
      showSnackBar("Success", "success", "Hospital saved successfully");
      setDialogOpen(false);
      loadHospitals();
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (rec) => {
    setItemToDelete(rec);
    setConfirmationDialogOpen(true);
  };

  const onDeleteConfirmed = async () => {
    try {
      await httpClient.delete(`${apiBase}/api/hospitals/${itemToDelete.id}`);
      showSnackBar("Success", "success", "Hospital deleted");
      loadHospitals();
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setConfirmationDialogOpen(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card>
          <MDBox display="flex" justifyContent="space-between" p={3}>
            <MDButton variant="gradient" color="info" onClick={openCreateDialog}>
              Create Hospital <Icon>add</Icon>
            </MDButton>
            <MDBox display="flex" gap={1}>
              <MDInput
                size="small"
                label="Search Hospital"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <MDButton variant="outlined" color="info" onClick={loadHospitals}>
                {t("search.go")}
              </MDButton>
            </MDBox>
          </MDBox>
          <MDBox px={3} pb={3}>
            {loading ? (
              <MDTypography variant="button">Loading...</MDTypography>
            ) : (
              <HospitalsTableData
                data={items}
                selectedIds={selectedIds}
                onToggle={(id) =>
                  setSelectedIds((prev) =>
                    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                  )
                }
                onEdit={(rec) => {
                  setEditingRecord(rec);
                  setDialogTitle("Edit Hospital");
                  setDialogOpen(true);
                }}
                onDelete={handleDelete}
              />
            )}
            <CustomTablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              totalItems={totalElements}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
            />
          </MDBox>
        </Card>
      </MDBox>

      <SchemaDialogForm
        open={dialogOpen}
        title={dialogTitle}
        schema={hospitalsFormSchema}
        initialRecord={editingRecord}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSubmit={onSubmit}
      />

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onOk={onDeleteConfirmed}
        onCancel={() => setConfirmationDialogOpen(false)}
        message={`Are you sure you want to delete ${itemToDelete?.name}?`}
      />

      <MDSnackbar
        color={snackBarColor}
        title="Notification"
        content={snackErrorMsg}
        open={snackBarOpen}
        onClose={() => setSnackBarOpen(false)}
      />
    </DashboardLayout>
  );
}

export default Hospitals;
